import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { UserPlus, Pencil, KeyRound, Trash2, ShieldCheck, Crown } from "lucide-react";
import { api, inputCls, labelCls } from "./posShared";
import { useAuth } from "../../context/AuthContext";

const emptyUser = { full_name: "", username: "", password: "", role: "cajero", phone: "", active: true, max_discount_percent: 0, can_issue_fiscal: false, register: "" };

export default function Usuarios() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [modal, setModal] = useState(null);

  const load = useCallback(() => api.get("/pos/users").then((r) => setUsers(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const save = async (u) => {
    try {
      if (u.id) {
        await api.put(`/pos/users/${u.id}`, {
          full_name: u.full_name, phone: u.phone, active: u.active,
          max_discount_percent: Number(u.max_discount_percent) || 0,
          can_issue_fiscal: !!u.can_issue_fiscal, register: u.register,
        });
        toast.success("Usuario actualizado");
      } else {
        if (!u.username || !u.password) return toast.error("Usuario y contraseña temporal requeridos");
        await api.post("/pos/users", {
          full_name: u.full_name, username: u.username, password: u.password, role: u.role, phone: u.phone,
          active: u.active, max_discount_percent: Number(u.max_discount_percent) || 0,
          can_issue_fiscal: !!u.can_issue_fiscal, register: u.register,
        });
        toast.success(u.role === "admin"
          ? "Administrador creado. Deberá cambiar su contraseña al iniciar sesión."
          : u.role === "visor"
          ? "Usuario de solo lectura creado. Deberá cambiar su contraseña al iniciar sesión."
          : "Cajero creado. Deberá cambiar su contraseña al iniciar sesión.");
      }
      setModal(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.put(`/pos/users/${u.id}`, { active: !u.active });
      toast.success(u.active ? "Usuario desactivado" : "Usuario activado");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const resetPass = async (u) => {
    const np = window.prompt(`Nueva contraseña temporal para ${u.username || u.email}:`);
    if (!np) return;
    try {
      await api.post(`/pos/users/${u.id}/reset-password`, { new_password: np });
      toast.success("Contraseña restablecida. El usuario deberá cambiarla al ingresar.");
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const del = async (u) => {
    if (!window.confirm(`¿Eliminar al usuario ${u.username || u.email}?`)) return;
    try {
      await api.delete(`/pos/users/${u.id}`);
      toast.success("Usuario eliminado");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-serif text-3xl">Usuarios de punto de venta</h1>
        <button data-testid="pos-add-user" onClick={() => setModal({ ...emptyUser })} className="inline-flex items-center gap-2 bg-espresso text-cream rounded-full px-5 py-2.5 text-xs uppercase tracking-[0.12em] hover:bg-rose transition"><UserPlus size={15} /> Nuevo usuario</button>
      </div>

      <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-[11px] uppercase text-neutral-500">
            <tr><th className="px-4 py-3 text-left">Nombre</th><th className="px-4 py-3 text-left">Usuario</th><th className="px-4 py-3 text-center">Rol</th><th className="px-4 py-3 text-left">Caja</th><th className="px-4 py-3 text-center">Desc. máx</th><th className="px-4 py-3 text-center">Fiscal</th><th className="px-4 py-3 text-center">Estado</th><th className="px-4 py-3 text-right">Acciones</th></tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const isAdmin = u.role === "admin";
              const isVisor = u.role === "visor";
              const roleLabel = isAdmin ? "Administrador" : isVisor ? "Solo lectura Web" : "Cajero";
              const hideCajeroFields = isAdmin || isVisor;
              const isPrimary = !!u.is_primary_admin;
              const isSelf = me && String(me.id) === String(u.id);
              return (
                <tr key={u.id} data-testid={`pos-user-row-${u.id}`} className="border-t border-neutral-50">
                  <td className="px-4 py-3">{u.full_name || u.name}{u.must_change_password && <span className="ml-2 text-[10px] text-amber-600">(contraseña temporal)</span>}</td>
                  <td className="px-4 py-3 text-neutral-600">{u.username || u.email || "—"}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${isAdmin ? "bg-rose/15 text-rose" : isVisor ? "bg-sky-50 text-sky-600" : "bg-neutral-100 text-neutral-600"}`}>
                      {isPrimary && <Crown size={11} />} {roleLabel}{isPrimary ? " · Principal" : ""}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-neutral-600">{u.register || "—"}</td>
                  <td className="px-4 py-3 text-center">{hideCajeroFields ? "—" : `${u.max_discount_percent || 0}%`}</td>
                  <td className="px-4 py-3 text-center">{isAdmin || u.can_issue_fiscal ? <ShieldCheck size={15} className="inline text-emerald-600" /> : "—"}</td>
                  <td className="px-4 py-3 text-center"><span className={`px-2 py-0.5 rounded-full text-xs ${u.active ? "bg-emerald-50 text-emerald-600" : "bg-neutral-100 text-neutral-500"}`}>{u.active ? "Activo" : "Inactivo"}</span></td>
                  <td className="px-4 py-3">
                    {isPrimary ? (
                      <div className="text-right text-[11px] text-neutral-400">Cuenta protegida</div>
                    ) : (
                      <div className="flex gap-1.5 justify-end">
                        <button data-testid={`pos-user-edit-${u.id}`} onClick={() => setModal({ ...u })} title="Editar" className="p-1.5 border border-neutral-200 rounded-lg hover:border-espresso"><Pencil size={13} /></button>
                        <button data-testid={`pos-user-reset-${u.id}`} onClick={() => resetPass(u)} title="Restablecer contraseña" className="p-1.5 border border-neutral-200 rounded-lg hover:border-espresso"><KeyRound size={13} /></button>
                        {!isSelf && <button data-testid={`pos-user-toggle-${u.id}`} onClick={() => toggleActive(u)} className={`p-1.5 border rounded-lg text-xs px-2 ${u.active ? "border-amber-300 text-amber-600" : "border-emerald-300 text-emerald-600"}`}>{u.active ? "Desactivar" : "Activar"}</button>}
                        {!isSelf && <button data-testid={`pos-user-del-${u.id}`} onClick={() => del(u)} title="Eliminar" className="p-1.5 border border-neutral-200 rounded-lg text-neutral-400 hover:text-red-500 hover:border-red-300"><Trash2 size={13} /></button>}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
            {users.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-neutral-400">No hay usuarios.</td></tr>}
          </tbody>
        </table>
      </div>

      {modal && <UserModal data={modal} onClose={() => setModal(null)} onSave={save} />}
    </div>
  );
}

function UserModal({ data, onClose, onSave }) {
  const [u, setU] = useState(data);
  const set = (k, v) => setU((p) => ({ ...p, [k]: v }));
  const isNew = !u.id;
  const isAdmin = u.role === "admin";
  const isVisor = u.role === "visor";
  const hideCajeroFields = isAdmin || isVisor;
  const title = isNew ? (isAdmin ? "Nuevo administrador" : isVisor ? "Nuevo usuario de solo lectura" : "Nuevo usuario") : "Editar usuario";
  return (
    <div className="fixed inset-0 z-[90] bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-md rounded-2xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-100">
          <h2 className="font-serif text-2xl">{title}</h2>
          <button data-testid="pos-user-close" onClick={onClose} className="text-neutral-400 text-xl hover:text-espresso">✕</button>
        </div>
        <div className="p-6 space-y-4">
          {isNew && (
            <div>
              <label className={labelCls}>Rol</label>
              <select data-testid="user-role" value={u.role} onChange={(e) => set("role", e.target.value)} className={inputCls}>
                <option value="cajero">Cajero</option>
                <option value="admin">Administrador</option>
                <option value="visor">Solo lectura Web</option>
              </select>
              {isAdmin && <p className="text-[11px] text-rose mt-1">Los administradores tienen acceso total al panel y al POS.</p>}
              {isVisor && <p className="text-[11px] text-sky-600 mt-1">Solo puede ver el panel Admin Web (sin editar). No tiene acceso al punto de venta.</p>}
            </div>
          )}
          <div><label className={labelCls}>Nombre completo</label><input data-testid="user-fullname" value={u.full_name} onChange={(e) => set("full_name", e.target.value)} className={inputCls} /></div>
          {isNew && <div><label className={labelCls}>Nombre de usuario</label><input data-testid="user-username" value={u.username} onChange={(e) => set("username", e.target.value)} className={inputCls} /></div>}
          {isNew && <div><label className={labelCls}>Contraseña temporal</label><input data-testid="user-password" value={u.password} onChange={(e) => set("password", e.target.value)} className={inputCls} /><p className="text-[11px] text-neutral-400 mt-1">Deberá cambiarla en su primer inicio de sesión.</p></div>}
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}>Teléfono (opcional)</label><input data-testid="user-phone" value={u.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} /></div>
            <div><label className={labelCls}>Caja / Sucursal</label><input data-testid="user-register" value={u.register} onChange={(e) => set("register", e.target.value)} className={inputCls} /></div>
          </div>
          {!hideCajeroFields && (
            <>
              <div><label className={labelCls}>Descuento máximo permitido (%)</label><input data-testid="user-maxdisc" type="number" min={0} max={100} value={u.max_discount_percent} onChange={(e) => set("max_discount_percent", e.target.value)} className={inputCls} /></div>
              <label className="flex items-center gap-2 text-sm"><input data-testid="user-fiscal" type="checkbox" checked={!!u.can_issue_fiscal} onChange={(e) => set("can_issue_fiscal", e.target.checked)} /> Puede emitir comprobantes fiscales (NCF)</label>
            </>
          )}
          <label className="flex items-center gap-2 text-sm"><input data-testid="user-active" type="checkbox" checked={!!u.active} onChange={(e) => set("active", e.target.checked)} /> Usuario activo</label>
          <button data-testid="user-save" onClick={() => onSave(u)} className="w-full bg-espresso text-cream rounded-full py-3 text-xs uppercase tracking-[0.15em] hover:bg-rose transition">{isNew ? (isAdmin ? "Crear administrador" : isVisor ? "Crear usuario de solo lectura" : "Crear cajero") : "Guardar cambios"}</button>
        </div>
      </div>
    </div>
  );
}
